import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueries, useQuery } from 'react-query';

import { firstOptionValue } from '../../../grouping/components/selectorOptions';
import { selectableLevels, toCca3 } from '../../../grouping/domain/cartographyGrouping';
import { getAreaNames, getCountryLevelsConfig } from '../../../grouping/services/cartographyGrouping.service';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { getCountryLabel } from '../../../MultiFlightMap.helpers';
import { countriesWithItems, locationCountry, matchesLocation, resolveLevels } from './locationFilters';
import { ALL_VALUE, nextPickedValues } from './multiSelection';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { CartographyItemInfo } from '../../../grouping/domain/cartographyGrouping';
import type { GroupFamily } from '../../../grouping/domain/grouping.types';
import type { PickedLevels } from './locationFilters';

type SelectorChange = SelectorOptionPropsInterface | SelectorOptionPropsInterface[];

export interface LevelFilterConfig {
  id: string;
  label: string;
  onChange: (options: SelectorChange) => void;
  options: SelectorOptionPropsInterface[];
  value: SelectorOptionPropsInterface[];
}

export interface CountryFilterConfig {
  label: string;
  onChange: (options: SelectorChange) => void;
  options: SelectorOptionPropsInterface[];
  value: SelectorOptionPropsInterface[];
}

export interface LocationFilterConfig {
  /** Number of location filters with something picked: a non default country and every level with areas. */
  activeCount: number;
  /** Country in use (shown in the section summary); `null` when the campaign has none. */
  country: { code: string; name: string } | null;
  /** Only in multi-country campaigns. */
  countrySelector: CountryFilterConfig | null;
  /** Something to clear: a country other than the default one or any picked level. */
  hasActive: boolean;
  levels: LevelFilterConfig[];
  onClear: () => void;
}

const LEVEL_COUNTRY = 'level0';
const FOREVER = Number.POSITIVE_INFINITY;

/** Country and cartography-level filters of the screens and venues lists (they only filter the list). */
export function useLocationFilters(
  family: GroupFamily,
  items: CartographyItemInfo[]
): { location: LocationFilterConfig; matches: (item: CartographyItemInfo) => boolean } {
  const { formatMessage } = useIntl();
  const { countries: campaignCountries } = useMultiFlightMap();
  const [pickedCountry, setPickedCountry] = useState<string | null>(null);
  const [pickedLevels, setPickedLevels] = useState<PickedLevels>({});

  const isMultiCountry = campaignCountries.length > 1;
  const countriesInUse = useMemo(
    () => countriesWithItems(family, items, campaignCountries),
    [family, items, campaignCountries]
  );
  const defaultCountry = countriesInUse[0] ?? toCca3(campaignCountries[0]);
  const country = pickedCountry && countriesInUse.includes(pickedCountry) ? pickedCountry : defaultCountry;

  // In single-country campaigns the country is only shown, never filtered
  const scope = useMemo(
    () => (isMultiCountry ? items.filter(item => locationCountry(family, item) === country) : items),
    [family, isMultiCountry, items, country]
  );

  const { data: levelsConfig } = useQuery(
    ['multiFlightMap-countryLevels', country],
    () => getCountryLevelsConfig(country ?? ''),
    { enabled: country !== null, staleTime: FOREVER }
  );
  const levelIds = useMemo(
    () => selectableLevels(levelsConfig ?? []).filter(({ id }) => id !== LEVEL_COUNTRY),
    [levelsConfig]
  );
  const levelKeys = useMemo(() => levelIds.map(({ id }) => id), [levelIds]);

  const { active, available } = useMemo(
    () => resolveLevels(scope, levelKeys, pickedLevels),
    [scope, levelKeys, pickedLevels]
  );
  // Names are fetched for every area of the country, so the cascade never changes the query
  const everyArea = useMemo(() => resolveLevels(scope, levelKeys, {}).available, [scope, levelKeys]);
  const namesByLevel = useQueries(
    levelKeys.map(level => ({
      enabled: country !== null && everyArea[level].length > 0,
      keepPreviousData: true,
      queryFn: () => getAreaNames(country ?? '', level, everyArea[level]),
      queryKey: ['multiFlightMap-areaNames', country, level, everyArea[level].join(',')],
      staleTime: FOREVER
    }))
  );

  const allOption: SelectorOptionPropsInterface = {
    id: 'all',
    label: formatMessage({ defaultMessage: 'All', id: 'multiFlightListsMap.filters.all' }),
    value: ALL_VALUE
  };

  const levels: LevelFilterConfig[] = levelIds.map(({ id, name }, index) => {
    const names = namesByLevel[index]?.data ?? {};
    const options = [
      allOption,
      ...available[id]
        .map(code => ({ id: `${id}-${code}`, label: names[code] ?? code, value: code }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];
    return {
      id,
      label: formatMessage({ defaultMessage: name, id: `adminBoundaries.boundaryType.${country}_${id}` }),
      onChange: selected =>
        setPickedLevels(previous => ({ ...previous, [id]: nextPickedValues(selected, active[id]) })),
      options,
      value: active[id].length ? options.filter(option => active[id].includes(String(option.value))) : [allOption]
    };
  });

  const countryOptions = countriesInUse.map(code => ({ id: code, label: getCountryLabel(code), value: code }));
  const countrySelector: CountryFilterConfig | null =
    isMultiCountry && countryOptions.length > 0
      ? {
          label: formatMessage({ defaultMessage: 'Country', id: 'multiFlightListsMap.filters.country.label' }),
          onChange: selected => {
            const code = firstOptionValue(selected);
            if (code) {
              setPickedCountry(code);
              setPickedLevels({});
            }
          },
          options: countryOptions,
          value: countryOptions.filter(option => option.value === country)
        }
      : null;

  const matches = useCallback(
    (item: CartographyItemInfo) =>
      matchesLocation(family, item, { active, country: isMultiCountry ? country : null, levelIds: levelKeys }),
    [family, active, isMultiCountry, country, levelKeys]
  );

  const activeLevelsCount = Object.values(active).filter(codes => codes.length > 0).length;
  const isCountryPicked = country !== defaultCountry;

  return {
    location: {
      activeCount: activeLevelsCount + (isCountryPicked ? 1 : 0),
      country: country ? { code: country, name: getCountryLabel(country) } : null,
      countrySelector,
      hasActive: isCountryPicked || activeLevelsCount > 0,
      levels,
      onClear: () => {
        setPickedCountry(null);
        setPickedLevels({});
      }
    },
    matches
  };
}
